/*
 * Upcart cart drawer: subscription support (SEC-563).
 *
 * The Upcart app (https://apps.shopify.com/upcart-cart-builder) replaces the theme's mini-cart
 * (sections/mini_cart.liquid) when its cart is enabled, and with it the two things that drawer
 * does for subscription carts:
 *
 *   1. Prices: a line carrying `_subscription_price` is shown at its subscription price, tiered
 *      by quantity from `_subscription_discount_1/2/3` (snippets/cart-item.liquid,
 *      sections/mini_cart.liquid). Upcart only knows Shopify's price.
 *   2. Checkout: a cart with any such line must go to /pages/subscription-checkout (MercadoPago),
 *      never to Shopify's native /checkout, after the T&C checkbox and the analytics event.
 *
 * (1) uses Upcart's `window.upcartModifyCart` hook, which receives a copy of the cart before
 * every render and may return a modified one. (2) can't use Upcart's checkout hooks: it runs
 * them, swallows any error they throw, then always navigates to its own checkout URL. So the
 * click on its checkout button is caught on `window` in the capture phase, before Upcart's
 * React handler sees it. That depends on Upcart's public class `upcart-checkout-button`.
 *
 * Inert without Upcart: nothing calls the hook and no element matches the selector.
 * Config comes from `window.SavvyUpcart`, written by layout/theme.liquid.
 */
(function () {
  'use strict';

  var config = window.SavvyUpcart || {};
  var SUBSCRIPTION_CHECKOUT_URL = config.subscriptionCheckoutUrl || '/pages/subscription-checkout';
  var MOBILE_CHECKOUT_URL = config.mobileCheckoutUrl || SUBSCRIPTION_CHECKOUT_URL;
  // Same breakpoint that shows #custom-checkout-btn-mobile (assets/mini-cart.css).
  var MOBILE_QUERY = '(max-width: 430px)';
  var CHECKOUT_BUTTON_CLASS = 'upcart-checkout-button';
  var TERMS_CHECKBOX_ID = 'savvy-terms-checkbox'; // Upcart custom HTML, "below checkout button"
  // The theme's cart page: it has its own T&C checkbox and "IR A PAGAR" button for subscription
  // carts (sections/main-cart.liquid), so it is where checkout goes when this script can't
  // safely decide or can't check T&C itself.
  var CART_URL = '/cart';
  var ANALYTICS_ENDPOINT = config.analyticsEndpoint || null;
  var ANALYTICS_TIMEOUT_MS = 3000;

  // One cart drawer only. While Upcart's cart is on, the theme's (#t4s-mini_cart) must stay
  // closed: after an add-to-cart the theme opens it through T4SThemeSP.Drawer.opend()
  // (interactable.min.js, afterActionATC "3") at the same time Upcart opens its own, and
  // snippets/product-recomendation.liquid shows its "Suma más bienestar" panel whenever
  // #t4s-mini_cart is open. Decided at open time: Upcart settles cartIsEnabled after this runs.
  var THEME_MINI_CART_ID = 't4s-mini_cart';

  // Fallback to the theme's drawer: Upcart counts as on only once its drawer is really on the
  // page - cart enabled *and* rendered into #upCart (in a shadow root, or directly). With the app
  // uninstalled, its embed off, its cart unpublished, or its script blocked or failing before it
  // renders, this is false and the theme's drawer opens exactly as it did before Upcart. A bare
  // `cartIsEnabled` isn't enough: it comes from Upcart's inline settings, which are there even
  // when the script that draws the drawer never runs.
  function upcartIsActive() {
    var settings = window.upcartSettings && window.upcartSettings.upcartEditorSettings;
    if (!settings || !settings.cartIsEnabled) {
      return false;
    }
    var root = document.getElementById('upCart');
    return !!(root && (root.shadowRoot || root.firstElementChild));
  }

  // T4SThemeSP.Drawer.close(event, trigger) closes the drawer named by the trigger's
  // `data-drawer-target` when more than one drawer is open (global.min.js); called with no
  // trigger in that case it throws. A stand-in trigger naming #t4s-mini_cart closes exactly
  // that drawer whether it is the only one open or not.
  var MINI_CART_TRIGGER = {
    attr: function () {
      return '#' + THEME_MINI_CART_ID;
    }
  };

  // Returns false when the theme isn't ready yet, so the caller can retry.
  function silenceThemeMiniCart() {
    var drawer = window.T4SThemeSP && window.T4SThemeSP.Drawer;
    var miniCart = document.getElementById(THEME_MINI_CART_ID);
    if (!drawer || !miniCart) {
      return false;
    }
    if (drawer.__savvyUpcart) {
      return true;
    }
    drawer.__savvyUpcart = true;
    var opend = drawer.opend;
    drawer.opend = function (target) {
      var element = target && (target[0] || target);
      if (element && element.id === THEME_MINI_CART_ID && upcartIsActive()) {
        return;
      }
      return opend.apply(this, arguments);
    };
    // Any other route that opens it (e.g. a [data-drawer-options] trigger Upcart doesn't
    // intercept): close it again and show Upcart's drawer instead.
    new MutationObserver(function () {
      if (miniCart.getAttribute('aria-hidden') !== 'false' || !upcartIsActive()) {
        return;
      }
      drawer.close(null, MINI_CART_TRIGGER);
      if (typeof window.upcartOpenCart === 'function') {
        window.upcartOpenCart();
      }
    }).observe(miniCart, { attributes: true, attributeFilter: ['aria-hidden'] });
    return true;
  }

  // global.min.js (which defines T4SThemeSP.Drawer) is deferred earlier in the page than this
  // script, so the first attempt normally succeeds; the retry on `load` covers it arriving late.
  function silenceThemeMiniCartWhenReady() {
    if (!silenceThemeMiniCart()) {
      window.addEventListener('load', silenceThemeMiniCart);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', silenceThemeMiniCartWhenReady);
  } else {
    silenceThemeMiniCartWhenReady();
  }

  // The cart Upcart last rendered, as received by upcartModifyCart (before our changes).
  var lastCart = null;
  var redirecting = false;
  // Back from the checkout page can restore this page from the bfcache mid-redirect.
  window.addEventListener('pageshow', function () { redirecting = false; });

  function isSubscriptionLine(item) {
    var value = item.properties && item.properties._subscription_price;
    return value !== undefined && value !== null && String(value) !== '';
  }

  function hasSubscription(cart) {
    return !!(cart && cart.items && cart.items.some(isSubscriptionLine));
  }

  // Tier by quantity, as in cart-item.liquid: 1 -> _1, 2 -> _2, 3+ -> _3. Values are strings.
  function discountKey(quantity) {
    if (quantity === 1) {
      return '_subscription_discount_1';
    } else if (quantity === 2) {
      return '_subscription_discount_2';
    } else {
      return '_subscription_discount_3';
    }
  }

  // Unit price after the tier discount, computed the way sections/mini_cart.liquid computes its
  // subtotal: `price | times: pct | divided_by: 100`, subtracted from `price`. Liquid's
  // `| times: 1` turns "15" into an integer and "15.5" into a float, and `divided_by` floors
  // only integer division - so floor for a whole percentage, keep the fraction otherwise.
  // Upcart needs whole hundredths, so the result is rounded once, at the line total, as the
  // `money` filter rounds the Liquid total.
  function subscriptionUnitPrice(item) {
    var raw = String(item.properties[discountKey(item.quantity)] || '').trim();
    var percent = parseFloat(raw);
    if (!isFinite(percent)) {
      return item.original_price;
    }
    var discount = item.original_price * percent / 100;
    if (/^-?\d+$/.test(raw)) {
      discount = Math.floor(discount);
    }
    return item.original_price - discount;
  }

  // Base is `original_price` (pre-discount, hundredths). The Liquid bumps it to the variant's
  // compare-at price when there is one, which the AJAX cart doesn't expose; no subscription
  // product had a compare-at price above its price when this was written (2026-10-08).
  function applySubscriptionPrices(cart) {
    var delta = 0;
    cart.items.forEach(function (item) {
      if (!isSubscriptionLine(item)) {
        return;
      }
      var unitPrice = subscriptionUnitPrice(item);
      var linePrice = Math.round(unitPrice * item.quantity);
      unitPrice = Math.round(unitPrice);
      delta += item.final_line_price - linePrice;
      item.final_price = unitPrice;
      item.discounted_price = unitPrice;
      item.final_line_price = linePrice;
      item.line_price = linePrice;
      item.total_discount = item.original_line_price - linePrice;
    });
    cart.total_price -= delta;
    cart.items_subtotal_price -= delta;
    cart.total_discount += delta;
    return cart;
  }

  var previousModifyCart = window.upcartModifyCart;
  window.upcartModifyCart = function (cart) {
    if (typeof previousModifyCart === 'function') {
      var modified = previousModifyCart(cart);
      if (modified && typeof modified === 'object') {
        cart = modified;
      }
    }
    lastCart = JSON.parse(JSON.stringify(cart));
    return hasSubscription(cart) ? applySubscriptionPrices(cart) : cart;
  };
  // Upcart may already have rendered before this deferred script ran.
  if (window.__upcartInitialized && typeof window.upcartRefreshCart === 'function') {
    window.upcartRefreshCart();
  }

  function findCheckoutButton(event) {
    var path = event.composedPath ? event.composedPath() : [event.target];
    for (var i = 0; i < path.length; i++) {
      var node = path[i];
      if (node.classList && node.classList.contains(CHECKOUT_BUTTON_CLASS)) {
        return node;
      }
    }
    return null;
  }

  function findTermsCheckbox(button) {
    var root = button.getRootNode ? button.getRootNode() : document;
    return (root.getElementById && root.getElementById(TERMS_CHECKBOX_ID)) || document.getElementById(TERMS_CHECKBOX_ID);
  }

  function showTermsRequired(checkbox) {
    var container = checkbox.closest('.savvy-terms') || checkbox.parentNode;
    var message = container.querySelector('.savvy-terms-required');
    if (!message) {
      message = document.createElement('p');
      message.className = 'savvy-terms-required';
      message.setAttribute('role', 'alert');
      message.style.cssText = 'color:#d72c0d;font-size:12px;margin:6px 0 0;';
      message.textContent = 'Debes aceptar los Términos y Condiciones para continuar.';
      container.appendChild(message);
      checkbox.addEventListener('change', function () {
        if (checkbox.checked && message.parentNode) {
          message.parentNode.removeChild(message);
        }
      });
    }
    checkbox.focus();
  }

  // Same event and payload as handleSubscriptionCheckout() in sections/mini_cart.liquid.
  function sendCheckoutEvent() {
    if (!ANALYTICS_ENDPOINT) {
      return Promise.resolve();
    }
    var request = fetch(ANALYTICS_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'evento_checkout',
        correo_cliente: config.customerEmail || null,
        tipo_evento: 'Inicio de checkout',
        id_cliente_shopify: config.customerId || null
      })
    }).catch(function (error) {
      console.error('Error al enviar la solicitud a Lambda:', error);
    });
    // Never hold the shopper on a slow analytics call.
    var timeout = new Promise(function (resolve) {
      setTimeout(resolve, ANALYTICS_TIMEOUT_MS);
    });
    return Promise.race([request, timeout]);
  }

  function redirectTo(url) {
    redirecting = true;
    window.location.href = url;
  }

  // Subscription cart, Upcart's button: T&C, analytics event, then the subscription checkout.
  function checkoutSubscription(button) {
    var terms = findTermsCheckbox(button);
    if (!terms) {
      // Without Upcart's T&C block there is nothing to check here, and going on would skip T&C
      // acceptance: send the shopper to the cart page, which asks for it before "IR A PAGAR".
      console.warn('[upcart-subscriptions] #' + TERMS_CHECKBOX_ID + ' not found in Upcart\'s drawer; sending a subscription cart to ' + CART_URL + ' for T&C.');
      redirectTo(CART_URL);
      return;
    }
    if (!terms.checked) {
      showTermsRequired(terms);
      return;
    }
    redirecting = true;
    var url = window.matchMedia(MOBILE_QUERY).matches ? MOBILE_CHECKOUT_URL : SUBSCRIPTION_CHECKOUT_URL;
    sendCheckoutEvent().then(function () {
      window.location.href = url;
    });
  }

  // Set while a click is re-dispatched to Upcart's own handler, so this listener lets it through.
  var passThrough = false;

  window.addEventListener('click', function (event) {
    if (passThrough) {
      return;
    }
    var button = findCheckoutButton(event);
    if (!button) {
      return;
    }
    if (lastCart && !hasSubscription(lastCart)) {
      return;
    }

    // Keep Upcart's own handler, which would navigate to Shopify's /checkout, from running.
    event.preventDefault();
    event.stopImmediatePropagation();
    if (redirecting) {
      return;
    }

    if (lastCart) {
      checkoutSubscription(button);
      return;
    }

    // No cart seen yet (Upcart rendered before upcartModifyCart was in place): read the cart
    // before letting anything through, so a subscription cart can't reach Shopify's /checkout.
    redirecting = true;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('HTTP ' + response.status);
        }
        return response.json();
      })
      .then(function (cart) {
        redirecting = false;
        lastCart = cart;
        if (hasSubscription(cart)) {
          checkoutSubscription(button);
        } else {
          passThrough = true;
          try {
            button.click();
          } finally {
            passThrough = false;
          }
        }
      })
      .catch(function (error) {
        // Can't tell which checkout applies: the cart page offers the right one either way.
        console.error('[upcart-subscriptions] Could not read /cart.js; sending the shopper to ' + CART_URL + '.', error);
        redirectTo(CART_URL);
      });
  }, true);
})();
